import { PropsWithChildren, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { IRootState } from '../store';
import { disconnectEcho, getEcho } from '../lib/echo';
import { setError, setStatus } from '../store/realtimeSlice';
import { useUserNotifications } from './useUserNotifications';

/**
 * Evento DOM custom disparado sempre que a conexÃ£o WebSocket volta a ficar
 * online depois de uma queda. Hooks de listagem escutam para refetchar e
 * reconciliar gaps (eventos perdidos enquanto offline).
 */
export const RECONCILED_EVENT = 'realtime:reconciled';

interface RealtimeErrorPayload {
    error?: {
        data?: {
            message?: string;
        };
    };
    message?: string;
}

interface RealtimeConnection {
    state?: string;
    bind: (event: string, callback: (payload?: unknown) => void) => void;
    unbind: (event: string, callback: (payload?: unknown) => void) => void;
}

interface RealtimePusher {
    connection?: RealtimeConnection;
}

interface EchoConnector {
    pusher?: RealtimePusher;
}

function NotificationsMount() {
    useUserNotifications();
    return null;
}

export function RealtimeProvider({ children }: PropsWithChildren) {
    const dispatch = useDispatch();
    const userId = useSelector((s: IRootState) => s.auth.user?.id ?? null);
    const wasConnectedRef = useRef(false);

    useEffect(() => {
        if (!userId) {
            disconnectEcho();
            dispatch(setStatus('disconnected'));
            wasConnectedRef.current = false;
            return;
        }

        const echo = getEcho();
        const pusher = (echo as { connector?: EchoConnector }).connector?.pusher;
        const connection = pusher?.connection;
        if (!connection) return;

        dispatch(setStatus(connection.state === 'connected' ? 'connected' : 'connecting'));

        const onConnecting = () => dispatch(setStatus('connecting'));
        const onConnected = () => {
            dispatch(setStatus('connected'));
            if (wasConnectedRef.current) {
                window.dispatchEvent(new CustomEvent(RECONCILED_EVENT));
            }
            wasConnectedRef.current = true;
        };
        const onUnavailable = () => dispatch(setStatus('reconnecting'));
        const onDisconnected = () => dispatch(setStatus('disconnected'));
        const onError = (err?: unknown) => {
            const payload = (err ?? {}) as RealtimeErrorPayload;
            const msg = payload.error?.data?.message || payload.message || 'Erro de conexÃ£o';
            dispatch(setError(String(msg)));
        };

        connection.bind('connecting', onConnecting);
        connection.bind('connected', onConnected);
        connection.bind('unavailable', onUnavailable);
        connection.bind('disconnected', onDisconnected);
        connection.bind('error', onError);

        return () => {
            connection.unbind('connecting', onConnecting);
            connection.unbind('connected', onConnected);
            connection.unbind('unavailable', onUnavailable);
            connection.unbind('disconnected', onDisconnected);
            connection.unbind('error', onError);
        };
    }, [userId, dispatch]);

    return (
        <>
            <NotificationsMount />
            {children}
        </>
    );
}
